/**
 * ElevenLabs Agents WebSocket client (ADR 0004, Variant A). Implemented
 * against the protocol shape described in docs/research/EXTERNAL_APIS.md —
 * that research flagged the exact message schema and audio encoding as
 * unconfirmed (elevenlabs.io was unreachable for a direct doc fetch during
 * research). This file isolates all of that protocol handling in one place
 * so it's a single spot to correct once verified against a live account
 * (see docs/SETUP.md "Sprachmodus" section) — nothing else in the app
 * depends on these wire details.
 *
 * **Stand nach Gegenprüfung (September 2026):** Die Nachrichtentypen sind
 * inzwischen gegen die aktuelle Dokumentation bestätigt —
 * `user_audio_chunk`, `conversation_initiation_client_data`, `agent_response`,
 * `audio`, `interruption`, `ping`/`pong` samt Feldnamen. Offen blieb allein
 * das Audioformat, weil es je Agent eingestellt wird; deshalb prüft der Client
 * die beim Verbindungsaufbau genannten Formate jetzt selbst und meldet eine
 * Abweichung im Klartext (siehe `checkAudioFormats` am Ende der Datei).
 *
 * Conventions:
 * - Outbound mic audio: 16 kHz, 16-bit signed PCM, mono, base64-encoded,
 *   sent as `{type: "user_audio_chunk", user_audio_chunk: "<base64>"}`.
 * - Inbound agent audio: base64 PCM chunks in `audio_event.audio_base_64`.
 * - Session identification for the Custom-LLM bridge: sent once, right
 *   after connecting, as `conversation_initiation_client_data` with
 *   `custom_llm_extra_body: { voiceToken }` — a short-lived signed token, not
 *   a raw session id, so the session cannot be chosen by the client.
 */
import { getAudioContext, resumeAudioContext } from './audioContext'
import { effektivwert, stimmhaftigkeit } from './dazwischenreden'
import { EchoFilter, FILTER_RATE } from './echoFilter'
import { voiceDiag, voiceExperiments } from './voiceDiagnostics'

export type VoiceClientEvent =
  | { type: 'connected' }
  | { type: 'disconnected' }
  | { type: 'user_transcript'; text: string }
  | { type: 'agent_text'; text: string }
  /** Nach einer Unterbrechung: der Teil der Antwort, der wirklich gesprochen wurde. */
  | { type: 'agent_text_correction'; text: string }
  | { type: 'agent_audio_chunk'; audioBase64: string }
  /** Ausgehandelte Formate aus conversation_initiation_metadata — der Player richtet sich danach. */
  | { type: 'audio_format'; agentOutput: unknown }
  | { type: 'interruption' }
  | { type: 'error'; message: string }

type Listener = (event: VoiceClientEvent) => void

export class ElevenLabsVoiceClient {
  private ws: WebSocket | null = null
  /** Wir haben selbst aufgelegt — das Schließen danach ist kein Fehler. */
  private beabsichtigt = false
  private listeners = new Set<Listener>()

  on(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private emit(event: VoiceClientEvent) {
    for (const listener of this.listeners) listener(event)
  }

  /**
   * @param options.firstMessage  Überschreibt die Begrüßung des Agenten für
   *   dieses Gespräch. Leer heißt: keine Begrüßung, K4ia wartet. Gesetzt wird
   *   das beim Wiederaufnehmen — wer „Sprechen beenden" und gleich wieder
   *   „Sprechen starten" drückt, will nicht ein zweites Mal hören, wer sie
   *   ist. Braucht im Agenten unter Sicherheit → Overrides die Freigabe für
   *   „First message"; ohne sie lehnt ElevenLabs die Verbindung mit 1008 ab.
   */
  connect(signedUrl: string, voiceToken: string, options: { firstMessage?: string } = {}): void {
    this.beabsichtigt = false
    this.ws = new WebSocket(signedUrl)

    this.ws.addEventListener('open', () => {
      const initiation: Record<string, unknown> = {
        type: 'conversation_initiation_client_data',
        custom_llm_extra_body: { voiceToken },
      }
      if (options.firstMessage !== undefined) {
        initiation['conversation_config_override'] = {
          agent: { first_message: options.firstMessage },
        }
      }
      this.ws?.send(JSON.stringify(initiation))
      this.emit({ type: 'connected' })
    })

    this.ws.addEventListener('message', (event: MessageEvent<string>) => {
      let payload: Record<string, unknown>
      try {
        payload = JSON.parse(event.data)
      } catch {
        return
      }
      this.handleMessage(payload)
    })

    this.ws.addEventListener('close', (event: CloseEvent) => {
      // Der Schließcode ist bei einer Verbindung, die sofort wieder abbricht,
      // die einzige Auskunft, die es überhaupt gibt — ElevenLabs nennt darin
      // den Grund. Ihn zu verschlucken macht aus einem benennbaren Fehler ein
      // „geht nicht".
      voiceDiag.geschlossen = `Code ${event.code}${event.reason ? `: ${event.reason}` : ''}`
      // Ein 1006 *nach* einem geführten Gespräch ist meist kein Fehler,
      // sondern das Ende: Der Agent beendet nach längerer Stille (Advanced →
      // „Turn timeout" / „Silence end call timeout"). Der Hinweis auf die
      // abgelaufene Adresse wäre hier schlicht falsch — die Verbindung stand
      // ja gerade noch.
      const grund = this.beabsichtigt
        ? null
        : event.code === 1006 && !event.reason && voiceDiag.antworten > 0
          ? 'Das Gespräch wurde beendet — meist, weil eine Weile niemand gesprochen hat (Agent: Advanced → Turn timeout / Silence end call timeout). Drück „Sprechen starten", um weiterzureden.'
          : describeCloseEvent(event)
      if (grund) this.emit({ type: 'error', message: grund })
      this.emit({ type: 'disconnected' })
    })
    this.ws.addEventListener('error', () =>
      this.emit({
        type: 'error',
        message:
          'Die Verbindung zu ElevenLabs kam nicht zustande. Häufigste Ursachen: abgelaufene signierte Adresse (gilt 15 Minuten) oder ein Agent, der nicht veröffentlicht ist.',
      }),
    )
  }

  private handleMessage(payload: Record<string, unknown>) {
    const type = payload['type'] as string
    voiceDiag.letzteNachricht = type
    switch (type) {
      // Beim Verbindungsaufbau nennt ElevenLabs die ausgehandelten Audioformate.
      // Bis hierher war das die einzige Annahme dieser Datei, die sich nicht aus
      // der Dokumentation belegen ließ — und die einzige, deren Bruch nicht als
      // Fehler auffällt, sondern als Rauschen.
      case 'conversation_initiation_metadata': {
        // Immer protokollieren, nicht nur bei Abweichung: Rauschen oder eine
        // seltsam klingende Stimme lassen sich ohne die tatsächlich
        // ausgehandelten Formate nicht eingrenzen, und raten kostet mehr Zeit
        // als eine Zeile im Log.
        console.warn(
          'K4ia Sprachmodus — ausgehandelte Formate:',
          payload['conversation_initiation_metadata_event'],
        )
        const meta = payload['conversation_initiation_metadata_event'] as
          Record<string, unknown> | undefined
        voiceDiag.formate = `${formatAlsText(meta?.['user_input_audio_format'])} → ${formatAlsText(meta?.['agent_output_audio_format'])}`
        this.emit({ type: 'audio_format', agentOutput: meta?.['agent_output_audio_format'] })
        const problem = checkAudioFormats(meta)
        if (problem) this.emit({ type: 'error', message: problem })
        break
      }

      case 'user_transcript': {
        const event = payload['user_transcription_event'] as
          { user_transcript?: string } | undefined
        // Eigene Markierungen ([[kamera:…]], [[foto]]) kommen als Transkript
        // zurück — die stehen im Chat schon als lesbarer Satz.
        if (event?.user_transcript && !event.user_transcript.startsWith('[[')) {
          voiceDiag.transkripte++
          this.emit({ type: 'user_transcript', text: event.user_transcript })
        }
        break
      }
      // Der Server sagt, ob er gerade Stimme hört (0…1). Das ist die eine
      // Zahl, die „ich spreche, aber nichts passiert" in zwei Fälle teilt:
      // kommt keine Stimme an (Mikrofon, Stummschaltung), oder kommt sie an
      // und der Beitrag wird trotzdem nicht abgeschlossen.
      case 'vad_score': {
        const score = (payload['vad_score_event'] as { vad_score?: number } | undefined)?.vad_score
        if (typeof score === 'number') voiceDiag.vadMax = Math.max(voiceDiag.vadMax, score)
        break
      }
      case 'agent_response': {
        const event = payload['agent_response_event'] as { agent_response?: string } | undefined
        if (event?.agent_response) {
          voiceDiag.antworten++
          this.emit({ type: 'agent_text', text: stripAudioTags(event.agent_response) })
        }
        break
      }
      case 'audio': {
        const event = payload['audio_event'] as { audio_base_64?: string } | undefined
        if (event?.audio_base_64) {
          voiceDiag.empfangenAudio++
          this.emit({ type: 'agent_audio_chunk', audioBase64: event.audio_base_64 })
        }
        break
      }
      case 'interruption':
        this.emit({ type: 'interruption' })
        break
      // Nach einer Unterbrechung schickt ElevenLabs, was tatsächlich zu hören
      // war — der Chat zeigt dann denselben Stand wie das Ohr, nicht den
      // ganzen geplanten Satz.
      case 'agent_response_correction': {
        const event = payload['agent_response_correction_event'] as
          { corrected_agent_response?: string } | undefined
        if (typeof event?.corrected_agent_response === 'string') {
          this.emit({
            type: 'agent_text_correction',
            text: stripAudioTags(event.corrected_agent_response),
          })
        }
        break
      }
      case 'ping': {
        const eventId = (payload['ping_event'] as { event_id?: number } | undefined)?.event_id
        this.ws?.send(JSON.stringify({ type: 'pong', event_id: eventId }))
        break
      }
      default:
        break
    }
  }

  /**
   * Eine Textnachricht im Namen des Besuchers — ElevenLabs behandelt sie wie
   * gesprochenen Text und fragt unseren Worker. Benutzt für den Kamera-Zug:
   * `[[kamera:<id>]]`, den die Brücke erkennt und in einen Showcase-Kontext
   * übersetzt. Kommt der Text als Transkript zurück, wird er unterdrückt.
   */
  sendUserMessage(text: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    this.ws.send(JSON.stringify({ type: 'user_message', text }))
  }

  sendAudioChunk(base64Pcm: string): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return
    this.ws.send(JSON.stringify({ type: 'user_audio_chunk', user_audio_chunk: base64Pcm }))
  }

  disconnect(): void {
    // Merken, *bevor* geschlossen wird: Das close-Ereignis kommt danach mit
    // Code 1005 („kein Status") und sah bisher aus wie ein Abbruch der
    // Gegenstelle — rot, mitten auf dem Bildschirm, nach einem Klick auf
    // „Sprechen beenden".
    this.beabsichtigt = true
    this.ws?.close(1000, 'Gespräch beendet')
    this.ws = null
  }
}

/** Mono-PCM16 als WAV-Datei. */
export function wavAusPcm16(pcm: Int16Array, rate: number): Blob {
  return new Blob([wavKopfFuerTest(pcm, rate).buffer as ArrayBuffer, pcm.buffer as ArrayBuffer], {
    type: 'audio/wav',
  })
}

/** Der 44-Byte-Kopf einer WAV-Datei (exportiert für den Test). */
export function wavKopfFuerTest(pcm: Int16Array, rate: number): DataView {
  const kopf = new DataView(new ArrayBuffer(44))
  const text = (o: number, t: string) => {
    for (let i = 0; i < t.length; i++) kopf.setUint8(o + i, t.charCodeAt(i))
  }
  text(0, 'RIFF')
  kopf.setUint32(4, 36 + pcm.byteLength, true)
  text(8, 'WAVE')
  text(12, 'fmt ')
  kopf.setUint32(16, 16, true)
  kopf.setUint16(20, 1, true)
  kopf.setUint16(22, 1, true)
  kopf.setUint32(24, rate, true)
  kopf.setUint32(28, rate * 2, true)
  kopf.setUint16(32, 2, true)
  kopf.setUint16(34, 16, true)
  text(36, 'data')
  kopf.setUint32(40, pcm.byteLength, true)
  return kopf
}

/** 40 ms bei 16 kHz: reicht für zwei Perioden einer tiefen Männerstimme (80 Hz). */
const STIMM_FENSTER = 640

/** Die Rate, in der ElevenLabs das Mikrofonsignal erwartet (`pcm_16000`). */
const ZIELRATE = 16000

/**
 * Nimmt das Mikrofon auf und liefert 16-kHz-Mono-PCM, base64-kodiert.
 *
 * Die Aufnahme läuft in der Rate, die der Browser vorgibt, und wird hier
 * heruntergerechnet — siehe Kommentar in `start()`, warum eine gewünschte Rate
 * nicht reicht.
 */
export class MicCapture {
  /**
   * **Stumm heißt: Stille senden, nicht nichts senden.**
   *
   * Das war der Grund, warum Halten nie funktioniert hat. ElevenLabs erkennt
   * das Ende eines Beitrags serverseitig aus dem Audiostrom (VAD): Stimme,
   * dann Stille — erst die Stille schließt den Beitrag ab und löst die Antwort
   * aus. Wir haben beim Loslassen den Strom einfach abgerissen. Der Server hat
   * die Stimme gehört, aber nie die Stille danach, und wartete auf ein Ende,
   * das nicht kam. Freihändig ging es, weil dort der Strom durchlief.
   *
   * Jetzt läuft der Strom immer. Solange nicht gehalten wird, gehen Blöcke aus
   * Nullen hinaus — gleiche Länge, gleicher Takt. Was im Raum gesprochen wird,
   * verlässt das Gerät dabei genauso wenig wie vorher: Nullen tragen nichts.
   */
  muted = false
  /**
   * Pegel jedes Blocks (Effektivwert, 0…1, nach dem Echofilter) — **auch
   * stummgeschaltet**. Die Erkennung fürs Dazwischenreden
   * (lib/dazwischenreden.ts) hört mit, während K4ia spricht; gesendet wird
   * dabei weiter nur Stille. `restEcho`: was der Filter von ihrem Echo noch im
   * Block vermutet, `null` ohne Filter, `Infinity`, solange er es noch lernt.
   */
  /**
   * Je Block: Pegel, Rest ihres Echos (Echofilter) und wie stimmhaft das
   * gefilterte Signal ist (0…1, `stimmhaftigkeit` — über die letzten 40 ms).
   */
  onRoh: ((rms: number, restEcho: number | null, stimmhaft: number) => void) | null = null
  /** Die letzten 40 ms gefiltert, für die Stimmhaftigkeit — ein Block allein ist am Telefon oft nur 16 ms. */
  private stimmFenster = new Float32Array(STIMM_FENSTER)
  /**
   * Pegel der Stimme des Besuchers nach dem Echofilter (0…1, wie `onLevel`) —
   * **auch stummgeschaltet**. Damit sieht der Besucher, dass K4ia ihn hört,
   * während sie spricht; gesendet wird dabei weiter nur Stille.
   */
  onHoeren: ((level: number) => void) | null = null
  /** Echofilter anhalten, solange geprüft wird, ob jemand dazwischenredet. */
  echoLernenAnhalten(an: boolean): void {
    if (this.filter) this.filter.angehalten = an
  }
  /** Läuft der eigene Echofilter (lib/echoFilter.ts)? */
  get echoUnterdrueckt(): boolean {
    return this.filter !== null
  }
  private filter: EchoFilter | null = null
  private referenz: AudioNode | null = null
  /**
   * Die letzte Sekunde echte Aufnahme, solange stummgeschaltet — nur im
   * Speicher, nie gesendet, außer `vorlaufSenden()` wird gerufen. Wer K4ia ins
   * Wort fällt, hat schon eine Viertelsekunde gesprochen, bis das feststeht;
   * ohne den Vorlauf fehlte der Spracherkennung das erste Wort.
   */
  private vorlauf: Float32Array[] = []
  private vorlaufLaenge = 0
  private senden: ((base64Pcm: string) => void) | null = null
  private stream: MediaStream | null = null
  private knoten: AudioNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private stumm: GainNode | null = null

  /**
   * @param onChunk  Audio fürs Netz.
   * @param onLevel  Lautstärke 0…1 für die Anzeige. Ohne sichtbare Rückmeldung
   *                 weiß niemand, ob das Mikrofon ihn gerade hört — im
   *                 Showroom steht man dann vor einem Bildschirm und redet ins
   *                 Ungewisse.
   * @param referenz Der Knoten, über den K4ias Stimme zum Lautsprecher geht
   *                 (PcmAudioPlayer.referenz). Mit ihm zieht der eigene
   *                 Echofilter ihre Stimme aus dem Mikrofon.
   */
  async start(
    onChunk: (base64Pcm: string) => void,
    onLevel?: (level: number) => void,
    referenz?: AudioNode | null,
  ): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        // **Aus, mit Absicht.** Mit Echounterdrückung legt iOS den gesamten
        // Ton auf seinen Telefonie-Pfad — andere Rate, eigene
        // Nachbearbeitung, auch der Wiedergabe —, und dort knackte K4ias
        // Stimme durchgehend. Ohne: sauber. Drei Varianten nebeneinander am
        // Gerät geprüft, nur diese eine machte den Unterschied.
        //
        // Der Preis: Spricht K4ia und der Besucher hält gleichzeitig den
        // Knopf, hört das Mikrofon auch sie. Beim Halten ist das tragbar —
        // das Mikrofon ist die meiste Zeit stumm, und wer dazwischenredet,
        // will genau das. `aec=1` in der Adresse schaltet zum Vergleich um.
        echoCancellation: voiceExperiments().aecAn,
        noiseSuppression: voiceExperiments().aecAn,
      },
    })
    // **Keine Wunschrate mehr.** `new AudioContext({ sampleRate: 16000 })` ist
    // eine Bitte, keine Zusage: Safari liefert weiterhin seine eigene Rate,
    // meist 48 kHz. Die Blöcke wurden dann als 16 kHz verschickt, obwohl sie
    // 48 kHz waren — dreifache Geschwindigkeit, und das klingt wie Rauschen.
    // Also die tatsächliche Rate nehmen und selbst herunterrechnen.
    const ctx = await resumeAudioContext()
    voiceDiag.context = `${ctx.state}, ${ctx.sampleRate} Hz`
    this.source = ctx.createMediaStreamSource(this.stream)

    // Durchgehend statt je Block: Dieselbe Naht, die die Wiedergabe knistern
    // ließ, entsteht auch hier — nur hört sie K4ia, nicht der Besucher. Alle
    // 4096 Samples ein Sprung im Signal, das ist elfmal je Sekunde, und die
    // Spracherkennung bekommt es als Erste ab.
    const herunter = new StreamResampler(ctx.sampleRate, ZIELRATE)
    this.senden = onChunk

    // Ihre Stimme wird genauso heruntergerechnet wie das Mikrofon — beide
    // Umrechnungen laufen im Gleichschritt, also bleiben sie übereinander.
    const refHerunter = new StreamResampler(ctx.sampleRate, FILTER_RATE)

    const verarbeiten = (roh: Float32Array, ref: Float32Array | null) => {
      let block = herunter.push(roh)
      let refBlock: Float32Array | null = null
      if (this.filter && ref) {
        // Ohne ihr Echo — alles Weitere (Senden, Vorlauf, Pegel) sieht nur
        // noch den Besucher. Der Filter gibt 16-ms-Schritte aus.
        refBlock = refHerunter.push(ref)
        block = this.filter.verarbeiten(block, refBlock)
      }
      if (block.length > 0) {
        if (this.muted) {
          onChunk(floatTo16BitPcmBase64(new Float32Array(block.length)))
          voiceDiag.gesendetStille++
          this.vorlauf.push(block)
          this.vorlaufLaenge += block.length
          while (
            this.vorlaufLaenge > ZIELRATE * VORLAUF_AUFNAHME_SEKUNDEN &&
            this.vorlauf.length > 1
          ) {
            this.vorlaufLaenge -= this.vorlauf.shift()!.length
          }
        } else {
          onChunk(floatTo16BitPcmBase64(block))
          voiceDiag.gesendetStimme++
        }
      }
      // Der Pegel kommt vom gefilterten Signal: Er soll den Besucher zeigen,
      // nicht K4ias Echo. Stummgeschaltet zeigt `onLevel` nichts — ein
      // mitlaufender Ring bei abgeschaltetem Mikrofon wäre eine Rückmeldung,
      // die lügt. `onHoeren` meldet ihn trotzdem: Während sie spricht, hört
      // das Gerät mit, ob jemand dazwischenredet, und das darf man sehen.
      if (block.length === 0) return
      const pegel = micLevel(block)
      if (onLevel) onLevel(this.muted ? 0 : pegel)
      this.onHoeren?.(pegel)
      // Mit Filter: verglichen wird mit dem Rest ihres Echos, den der Filter
      // selbst schätzt — genauer als ihr Pegel mal ein gelernter Anteil.
      // Solange er ihr Echo noch lernt, gibt es keinen verlässlichen Maßstab:
      // `Infinity` heißt dann „nichts ist Stimme“ — unterbrechen per Tipp geht.
      const rest = this.filter && refBlock ? this.filter.restEcho() : null
      if (this.filter) {
        voiceDiag.echo = `Echofilter, ${this.filter.bereit ? 'eingelernt' : 'lernt noch'}${
          this.filter.zurueckgesetzt ? `, ${this.filter.zurueckgesetzt}× neu` : ''
        }`
      }
      if (block.length >= STIMM_FENSTER) {
        this.stimmFenster = block.slice(block.length - STIMM_FENSTER)
      } else {
        this.stimmFenster.copyWithin(0, block.length)
        this.stimmFenster.set(block, STIMM_FENSTER - block.length)
      }
      this.onRoh?.(
        effektivwert(block),
        rest === null || this.filter?.bereit ? rest : Number.POSITIVE_INFINITY,
        stimmhaftigkeit(this.stimmFenster, ZIELRATE),
      )
    }

    // **Aufnahme im Audio-Thread, wo es geht.** Der ScriptProcessorNode läuft
    // im Haupt-Thread — neben React, der fliegenden Figur, dem Pegelring und
    // jeder ausgepackten Nachricht von ElevenLabs. Verpasst er seinen Takt,
    // reißt das in Safari den ganzen Audiographen mit, also auch die
    // Wiedergabe. Am Rechner passiert das selten, auf dem Telefon ständig —
    // und es klingt wie Knacken in *ihrer* Stimme, obwohl es unser Mikrofon
    // ist. Deshalb zuerst das Worklet (public/audio/mic-worklet.js); der
    // ScriptProcessor bleibt nur als Rückfall für Browser ohne Worklet.
    const worklet = await this.workletKnoten(ctx, verarbeiten)
    this.knoten = worklet ?? this.scriptKnoten(ctx, verarbeiten)
    this.source.connect(this.knoten)
    // Der Echofilter nur mit Referenz und nur im Worklet — nur dort kommen
    // Mikrofon und ihre Stimme im selben Takt an.
    if (worklet && referenz) {
      referenz.connect(worklet, 0, 1)
      this.referenz = referenz
      this.filter = new EchoFilter()
    }
    voiceDiag.echo = this.filter ? 'Echofilter' : referenz ? 'nicht verfügbar' : 'aus'

    // Beide Knoten arbeiten nur, wenn ihr Ausgang am Ziel hängt — sie
    // schreiben aber nichts hinein. Über einen Verstärker auf null geht das
    // sicher: Käme doch etwas durch, wäre es das Mikrofon auf dem Lautsprecher,
    // also eine Rückkopplung mitten im Gespräch.
    this.stumm = ctx.createGain()
    this.stumm.gain.value = 0
    this.knoten.connect(this.stumm)
    this.stumm.connect(ctx.destination)
  }

  private async workletKnoten(
    ctx: AudioContext,
    verarbeiten: (block: Float32Array, ref: Float32Array | null) => void,
  ): Promise<AudioWorkletNode | null> {
    if (!('audioWorklet' in ctx) || typeof AudioWorkletNode === 'undefined') return null
    try {
      // `?v=2`: Der Worklet hat seit dem Dazwischenreden einen zweiten Eingang;
      // eine zwischengespeicherte alte Fassung schickt nur das Mikrofon.
      await ctx.audioWorklet.addModule('/audio/mic-worklet.js?v=2')
      const node = new AudioWorkletNode(ctx, 'k4ia-mic', {
        numberOfInputs: 2,
        numberOfOutputs: 1,
        channelCount: 1,
      })
      node.port.onmessage = (
        e: MessageEvent<Float32Array | { mic: Float32Array; ref: Float32Array }>,
      ) =>
        e.data instanceof Float32Array
          ? verarbeiten(e.data, null)
          : verarbeiten(e.data.mic, e.data.ref)
      voiceDiag.aufnahme = 'AudioWorklet'
      return node
    } catch (err) {
      // Nicht verschweigen: Läuft die Aufnahme im Rückfall, will man das im
      // Log sehen, wenn es wieder knackt.
      console.warn(
        'K4ia Sprachmodus: AudioWorklet nicht verfügbar, Rückfall auf ScriptProcessor.',
        err,
      )
      return null
    }
  }

  private scriptKnoten(
    ctx: AudioContext,
    verarbeiten: (block: Float32Array, ref: Float32Array | null) => void,
  ): AudioNode {
    const processor = ctx.createScriptProcessor(4096, 1, 1)
    processor.onaudioprocess = (e) => verarbeiten(e.inputBuffer.getChannelData(0), null)
    voiceDiag.aufnahme = 'ScriptProcessor (Rückfall)'
    return processor
  }

  /**
   * Schickt die zuletzt stumm aufgenommenen ~350 ms hinterher — genau einmal,
   * im Moment, in dem das Dazwischenreden feststeht und das Mikrofon aufgeht.
   */
  vorlaufSenden(): void {
    if (this.senden) {
      for (const block of this.vorlauf) {
        this.senden(floatTo16BitPcmBase64(block))
        voiceDiag.gesendetStimme++
      }
    }
    this.vorlaufLeeren()
  }

  /**
   * Die letzten `sekunden` des Vorlaufs als WAV (16 kHz, 16 Bit) — für die
   * Prüfung, ob ein kurzer Laut ein Stichwort war. Nichts wird gesendet oder
   * geleert; `null`, wenn nichts da ist.
   */
  vorlaufWav(sekunden: number): Blob | null {
    const gesamt = Math.min(this.vorlaufLaenge, Math.round(ZIELRATE * sekunden))
    if (gesamt <= 0) return null
    const pcm = new Int16Array(gesamt)
    let pos = gesamt
    for (let i = this.vorlauf.length - 1; i >= 0 && pos > 0; i--) {
      const block = this.vorlauf[i]!
      const n = Math.min(block.length, pos)
      for (let j = 0; j < n; j++) {
        const v = Math.max(-1, Math.min(1, block[block.length - n + j]!))
        pcm[pos - n + j] = v < 0 ? v * 0x8000 : v * 0x7fff
      }
      pos -= n
    }
    return wavAusPcm16(pcm, ZIELRATE)
  }

  /** Vorlauf verwerfen — wenn das Mikrofon aus anderem Grund aufgeht. */
  vorlaufLeeren(): void {
    this.vorlauf = []
    this.vorlaufLaenge = 0
  }

  stop(): void {
    this.vorlaufLeeren()
    this.senden = null
    this.onRoh = null
    this.onHoeren = null
    if (this.referenz && this.knoten) {
      try {
        this.referenz.disconnect(this.knoten)
      } catch {
        // War nicht (mehr) verbunden.
      }
    }
    this.referenz = null
    this.filter = null
    if (this.knoten && 'onaudioprocess' in this.knoten) {
      ;(this.knoten as ScriptProcessorNode).onaudioprocess = null
    }
    if (this.knoten && 'port' in this.knoten) {
      ;(this.knoten as AudioWorkletNode).port.onmessage = null
    }
    this.knoten?.disconnect()
    this.source?.disconnect()
    this.stumm?.disconnect()
    this.stream?.getTracks().forEach((t) => t.stop())
    // Der Context gehört uns nicht allein — er wird geteilt und beim Beenden
    // des Gesprächs angehalten, nicht hier geschlossen.
    this.knoten = null
    this.source = null
    this.stumm = null
    this.stream = null
  }
}

function floatTo16BitPcmBase64(float32: Float32Array): string {
  const buffer = new ArrayBuffer(float32.length * 2)
  const view = new DataView(buffer)
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]!))
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true)
  }
  let binary = ''
  const bytes = new Uint8Array(buffer)
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary)
}

/**
 * Vorlauf gegen Jitter. Am Kabel reichen 120 ms, im Mobilfunk nicht: Dort
 * kommen Stücke in Schüben, und jedes zu spät gekommene Stück ist eine echte
 * Lücke mitten im Wort. 200 ms kosten Antwortzeit, die niemand bemerkt, und
 * sparen ein Knacken, das jeder bemerkt.
 */
const VORLAUF_SEKUNDEN = 0.2

/**
 * So viel stumme Aufnahme hält MicCapture für das Dazwischenreden vor: der
 * erste Ansatz, bis der Verdacht steht (~250 ms), und die Prüfung, während
 * K4ia leiser ist (mindestens 1 s, DazwischenAblauf) — mit Luft. Sonst fehlte
 * der Spracherkennung der Anfang des Satzes, der sie unterbrochen hat.
 */
const VORLAUF_AUFNAHME_SEKUNDEN = 2

/**
 * Kleinere Stücke werden gesammelt, bevor sie eingeplant werden. Jedes
 * eingeplante Stück ist ein eigener Knoten; dreissig Knoten je Sekunde sind auf
 * dem Telefon spürbar mehr Arbeit als fünf.
 */
const MINDESTSTUECK_SEKUNDEN = 0.06

/**
 * So laut ist sie, während geprüft wird, ob jemand dazwischenredet: rund
 * 16 dB leiser — deutlich genug, dass der Besucher über ihrem Echo liegt,
 * leise genug, dass man hört: Sie hat es gemerkt.
 */
const DUCK_PEGEL = 0.35

/** So kurz vor „jetzt" plant niemand mehr ein — das Telefon käme nicht mit. */
const ZU_KNAPP_SEKUNDEN = 0.02

/**
 * Spielt die base64-kodierten PCM-Stücke des Agenten lückenlos hintereinander
 * ab — in einem gemeinsamen AudioContext, mit eigener, durchlaufender
 * Umrechnung und einem Jitterpuffer davor.
 *
 * Die Abtastrate ist einstellbar und wird aus `conversation_initiation_metadata`
 * gesetzt, statt fest 16 kHz anzunehmen: Spielt man 24-kHz-Material als 16 kHz
 * ab, klingt die Stimme zu tief und verwaschen — was sich wie ein Problem der
 * Stimme anhört und keines ist.
 */
export class PcmAudioPlayer {
  /**
   * Eingehende Stücke verwerfen, bis der nächste Beitrag beginnt.
   *
   * Drückt der Besucher den Knopf, während K4ia spricht, wird sie sofort
   * still (stopAll). ElevenLabs schickt den Rest ihres Satzes aber trotzdem
   * noch — erst wenn es Stimme hört, bricht es serverseitig ab. Ohne diese
   * Sperre setzte sie nach dem Stillwerden mitten im Satz wieder ein, mit dem
   * Teil, der gerade angekommen war. Zurückgesetzt wird beim nächsten
   * Transkript oder wenn der Server den Abbruch selbst meldet.
   */
  verwerfen = false
  /**
   * Wird gerufen, wenn das letzte eingeplante Stück zu Ende ist und nichts
   * mehr wartet — K4ia hat ausgeredet. Der Sprachmodus öffnet danach nach
   * der Begrüßung einmal das Mikrofon von selbst.
   */
  onIdle: (() => void) | null = null
  private nextStartTime = 0
  private sampleRate = 16000
  /** Laufende Umrechnung über Stückgrenzen hinweg — siehe StreamResampler. */
  private resampler: StreamResampler | null = null
  private warteschlange: Float32Array[] = []
  private warteLaenge = 0
  private laufende = new Set<AudioBufferSourceNode>()
  private nachzuegler: ReturnType<typeof setTimeout> | null = null
  /**
   * Alles, was sie sagt, läuft über diesen Knoten zum Lautsprecher — und am
   * Messknoten vorbei, der sagt, wie laut sie in diesem Moment ist. Die
   * Erkennung fürs Dazwischenreden vergleicht das mit dem Mikrofon.
   */
  private ausgang: GainNode | null = null
  private messung: AnalyserNode | null = null
  private messPuffer: Float32Array<ArrayBuffer> | null = null

  /** Nur zum Prüfen — die Rate selbst wird über setFormat gesetzt. */
  get rate(): number {
    return this.sampleRate
  }

  /**
   * Während der Berührung aufrufen, die das Gespräch startet: Danach steht der
   * Context, und das erste Audiostück muss nicht erst auf eine Erlaubnis
   * warten, die es ohne Geste nicht mehr gibt.
   */
  async prepare(): Promise<void> {
    await resumeAudioContext()
  }

  /** Aus dem ausgehandelten Format, z. B. `pcm_24000`. Unbekanntes lässt die Rate, wie sie ist. */
  setFormat(format: unknown): void {
    if (typeof format !== 'string') return
    const treffer = /^pcm_(\d+)$/.exec(format)
    if (!treffer) return
    const rate = Number(treffer[1])
    if (rate === this.sampleRate) return
    this.sampleRate = rate
    this.stopAll()
  }

  enqueue(base64Pcm: string): void {
    const ctx = getAudioContext()
    const eingang = pcm16ToFloat(base64Pcm)
    if (eingang.length === 0) return

    // **Selbst umrechnen, nicht den Browser jeden Puffer einzeln.** Ein
    // AudioBuffer mit 16 kHz in einem 48-kHz-Context wird beim Abspielen
    // hochgerechnet — aber für jeden Puffer getrennt, ohne Wissen über den
    // vorherigen. An jeder Stückgrenze entsteht dadurch ein kleiner Sprung,
    // und bei dreissig Stücken je Sekunde ist das genau das Knistern. Ein
    // Vorlauf half dagegen nicht, weil es keine Lücke ist, sondern ein
    // Kantenfehler.
    //
    // Der StreamResampler führt die Bruchteil-Position und das letzte Sample
    // über die Stücke hinweg mit, sodass die Umrechnung durchläuft, als wäre
    // es ein einziger Strom.
    this.resampler ??= new StreamResampler(this.sampleRate, ctx.sampleRate)
    const ausgang = this.resampler.push(eingang)
    if (ausgang.length === 0) return

    this.warteschlange.push(ausgang)
    this.warteLaenge += ausgang.length
    this.plan(ctx, false)
  }

  /**
   * Plant das Gesammelte als **ein** Stück ein.
   *
   * `sofort` überspringt die Mindestlänge — sonst bliebe das letzte, kurze
   * Stück eines Satzes liegen und das Wortende fehlte.
   */
  private plan(ctx: AudioContext, sofort: boolean): void {
    if (this.nachzuegler) {
      clearTimeout(this.nachzuegler)
      this.nachzuegler = null
    }
    if (this.warteLaenge === 0) return

    if (!sofort && this.warteLaenge < ctx.sampleRate * MINDESTSTUECK_SEKUNDEN) {
      this.nachzuegler = setTimeout(() => this.plan(ctx, true), MINDESTSTUECK_SEKUNDEN * 1000)
      return
    }

    const gesamt = new Float32Array(this.warteLaenge)
    let offset = 0
    for (const teil of this.warteschlange) {
      gesamt.set(teil, offset)
      offset += teil.length
    }
    this.warteschlange = []
    this.warteLaenge = 0

    const audioBuffer = ctx.createBuffer(1, gesamt.length, ctx.sampleRate)
    audioBuffer.getChannelData(0).set(gesamt)

    const source = ctx.createBufferSource()
    source.buffer = audioBuffer
    source.connect(this.ausgangsknoten(ctx))
    source.onended = () => {
      this.laufende.delete(source)
      if (this.laufende.size === 0 && this.warteLaenge === 0 && !this.nachzuegler) {
        this.onIdle?.()
      }
    }

    const jetzt = ctx.currentTime
    if (this.nextStartTime < jetzt + ZU_KNAPP_SEKUNDEN) {
      // Nicht das erste Stück, sondern eines, das zu spät kam: Das ist eine
      // hörbare Lücke — und die Zahl, die sagt, ob das Knacken Jitter ist.
      // Nur, wenn der Strom gerade eben noch lief. Nach einer Sprechpause
      // fängt K4ia neu an — das ist kein Unterlauf, sondern der nächste Satz.
      if (this.nextStartTime > 0 && jetzt - this.nextStartTime < 1) voiceDiag.unterlaeufe++
      this.nextStartTime = jetzt + VORLAUF_SEKUNDEN
    } else {
      voiceDiag.vorlaufMinMs = Math.min(voiceDiag.vorlaufMinMs, (this.nextStartTime - jetzt) * 1000)
    }
    source.start(this.nextStartTime)
    this.nextStartTime += audioBuffer.duration
    this.laufende.add(source)
  }

  private ausgangsknoten(ctx: AudioContext): AudioNode {
    if (!this.ausgang || this.ausgang.context !== ctx) {
      this.ausgang = ctx.createGain()
      this.messung = ctx.createAnalyser()
      this.messung.fftSize = 2048
      this.messPuffer = new Float32Array(this.messung.fftSize)
      this.ausgang.connect(ctx.destination)
      this.ausgang.connect(this.messung)
    }
    return this.ausgang
  }

  /**
   * Der Knoten, über den ihre Stimme zum Lautsprecher geht — für die
   * Echofilter des Mikrofons (MicCapture.start). Gleicher Context,
   * gleicher Takt: Was hier durchläuft, ist genau das, was der Lautsprecher spielt.
   */
  referenz(): AudioNode {
    return this.ausgangsknoten(getAudioContext())
  }

  /** Wie laut sie gerade spricht (Effektivwert der letzten ~40 ms, 0…1). */
  pegel(): number {
    if (!this.messung || !this.messPuffer || this.laufende.size === 0) return 0
    this.messung.getFloatTimeDomainData(this.messPuffer)
    return effektivwert(this.messPuffer)
  }

  /**
   * Barge-in: sofort still und die Warteschlange verwerfen.
   *
   * **Der Context bleibt dabei bestehen.** Ihn hier zu schließen und beim
   * nächsten Stück neu zu bauen war ein eigener Fehler: Jedes Dazwischenreden
   * baute die gesamte Audiokette neu auf — auf dem Telefon hörbar, und der
   * neue Context entstand ohne Berührung, durfte also gar nicht von selbst
   * loslegen.
   */
  stopAll(): void {
    if (this.nachzuegler) {
      clearTimeout(this.nachzuegler)
      this.nachzuegler = null
    }
    for (const source of this.laufende) {
      try {
        source.stop()
      } catch {
        // Ein Knoten, der noch nicht lief oder schon fertig ist — nicht der
        // Rede wert.
      }
    }
    this.laufende.clear()
    this.warteschlange = []
    this.warteLaenge = 0
    this.resampler = null
    this.nextStartTime = 0
    // Der nächste Satz beginnt wieder in voller Lautstärke.
    if (this.ausgang) {
      this.ausgang.gain.cancelScheduledValues(0)
      this.ausgang.gain.value = 1
    }
  }

  /**
   * Leiser werden, weil jemand ansetzt — oder wieder laut, weil es nichts war
   * (DazwischenAblauf). Schnell hinunter (~20 ms), sanft wieder hinauf
   * (~80 ms), damit es nach Zuhören klingt und nicht nach Aussetzer.
   */
  ducken(leise: boolean): void {
    if (!this.ausgang) return
    const ctx = this.ausgang.context
    const g = this.ausgang.gain
    g.cancelScheduledValues(ctx.currentTime)
    g.setTargetAtTime(leise ? DUCK_PEGEL : 1, ctx.currentTime, leise ? 0.02 : 0.08)
  }
}

/**
 * Entfernt Audio-Tags wie `[happy]` oder `[laughs]` aus dem Antworttext.
 *
 * Der expressive Modus des v3-Modells streut sie ein; ElevenLabs spricht sie
 * als Stimmung, im Transkript stünden sie als Klammertext („[happy] Gerne
 * helfe ich dir"). Nur kurze Wortfolgen in eckigen Klammern — ein Zitat wie
 * „[Quelle 2]" bleibt, weil Ziffern nicht dazugehören.
 */
export function stripAudioTags(text: string): string {
  return text
    .replace(/\[[A-Za-zÄÖÜäöüß]+(?: [A-Za-zÄÖÜäöüß]+)?\]\s?/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/** 16-Bit-PCM (little endian, base64) in Float-Samples. */
export function pcm16ToFloat(base64: string): Float32Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  const view = new DataView(bytes.buffer)
  const anzahl = Math.floor(bytes.length / 2)
  const out = new Float32Array(anzahl)
  for (let i = 0; i < anzahl; i++) out[i] = view.getInt16(i * 2, true) / 0x8000
  return out
}

/**
 * Rechnet einen fortlaufenden Audiostrom von einer Rate auf eine andere um und
 * **behält den Zustand zwischen den Stücken**.
 *
 * Genau darin liegt der Unterschied zu einer Umrechnung je Stück: Die
 * Bruchteil-Leseposition und die letzten Samples laufen weiter, sodass an der
 * Naht zwischen zwei Stücken kein Sprung entsteht. Ohne das knistert jede
 * Grenze — unabhängig davon, wie pünktlich die Daten ankommen.
 */
export class StreamResampler {
  private readonly verhaeltnis: number
  private rest = new Float32Array(0)
  private position = 0

  constructor(von: number, nach: number) {
    this.verhaeltnis = von / nach
  }

  push(input: Float32Array): Float32Array {
    if (this.verhaeltnis === 1) return input

    const puffer = new Float32Array(this.rest.length + input.length)
    puffer.set(this.rest)
    puffer.set(input, this.rest.length)

    const out: number[] = []
    let p = this.position
    while (p + 1 < puffer.length) {
      const i = Math.floor(p)
      const anteil = p - i
      out.push(puffer[i]! * (1 - anteil) + puffer[i + 1]! * anteil)
      p += this.verhaeltnis
    }

    // Was noch gebraucht wird, bleibt liegen: das Sample links der nächsten
    // Leseposition und alles danach.
    //
    // `verbraucht` muss auf die Pufferlänge begrenzt werden, *bevor* die neue
    // Position daraus entsteht: Steht die Leseposition schon hinter dem Puffer
    // (bei starkem Herunterrechnen übersprungene Samples), wirft die Begrenzung
    // sonst nur beim Abschneiden, nicht beim Rechnen — die Position verliert
    // genau die Samples, die übersprungen werden sollten, und die Ausgabe
    // verschiebt sich an jeder Stückgrenze um ein Sample. Das ist derselbe
    // Kantenfehler, gegen den diese Klasse überhaupt gebaut ist.
    const verbraucht = Math.min(Math.floor(p), puffer.length)
    this.rest = puffer.slice(verbraucht)
    this.position = p - verbraucht
    return Float32Array.from(out)
  }
}

/** Für die Debug-Anzeige: ein Format als Text, Unbekanntes als Fragezeichen. */
function formatAlsText(format: unknown): string {
  return typeof format === 'string' ? format : '?'
}

/** Das Format, das MicRecorder aufnimmt und der Player erwartet. */
export const ERWARTETES_AUDIOFORMAT = 'pcm_16000'

/**
 * Vergleicht die vom Agenten ausgehandelten Audioformate mit dem, was dieser
 * Client tatsächlich sendet und abspielt.
 *
 * Stimmen sie nicht überein, hört man kein Fehlerbild, sondern Rauschen oder
 * eine verzerrte Stimme — und sucht den Fehler dann überall, nur nicht in einer
 * Einstellung des Agenten. Deshalb die ausdrückliche Prüfung mit einer Meldung,
 * die sagt, wo man sie ändert.
 *
 * Gibt `null` zurück, wenn alles passt oder das Feld fehlt (dann ist nichts
 * belegt und eine Warnung wäre geraten).
 */
export function checkAudioFormats(metadata: unknown): string | null {
  if (typeof metadata !== 'object' || metadata === null) return null
  const m = metadata as Record<string, unknown>
  const eingang = m['user_input_audio_format']
  const ausgang = m['agent_output_audio_format']

  // **Fehlende Angabe ist kein grünes Licht.** Nennt ElevenLabs das Format
  // nicht, spielen wir mit einer Annahme ab — und eine falsche Annahme klingt
  // nicht nach Fehler, sondern nach einer anderen, verrauschten Stimme. Das
  // gehört gesagt, nicht verschwiegen.
  if (typeof ausgang !== 'string') {
    return `ElevenLabs nennt kein Ausgabeformat. Es wird ${ERWARTETES_AUDIOFORMAT} angenommen — stimmt das nicht, klingt die Stimme tiefer, verwaschen und knackt. Im Agenten unter Stimmen → Allgemeine Stimmeinstellungen das TTS-Ausgabeformat auf ${ERWARTETES_AUDIOFORMAT} stellen.`
  }

  const falsch: string[] = []
  if (typeof eingang === 'string' && eingang !== ERWARTETES_AUDIOFORMAT) {
    falsch.push(`Eingang ${eingang}`)
  }
  // Am Ausgang ist jede PCM-Rate recht: Der Player richtet sich nach dem
  // ausgehandelten Format und rechnet selbst um. Höhere Raten (pcm_24000,
  // pcm_44100) klingen voller als 16 kHz — das ist eine Einstellung des
  // Agenten, keine des Codes. Nur ein anderes Kodierverfahren (mp3, ulaw)
  // kann der Player nicht lesen.
  if (typeof ausgang === 'string' && !/^pcm_\d+$/.test(ausgang)) {
    falsch.push(`Ausgang ${ausgang}`)
  }
  if (falsch.length === 0) return null

  return `Audioformat passt nicht: Der Agent erwartet ${falsch.join(' und ')}, dieser Client arbeitet mit ${ERWARTETES_AUDIOFORMAT}. Im ElevenLabs-Dashboard beim Agenten auf ${ERWARTETES_AUDIOFORMAT} stellen — sonst kommt statt Sprache nur Rauschen.`
}

/**
 * Gründe, zu denen wir mehr wissen als die Gegenstelle sagt — und nur zu
 * diesen. Jeder Eintrag entstand aus einem echten Fehlschlag.
 */
const HILFE_ZU_GRUND: [RegExp, string][] = [
  [
    /custom_llm_extra_body|extra body override/i,
    'Im Agenten unter Sicherheit → Overrides „Custom LLM extra body" freigeben und neu veröffentlichen. Darüber reicht der Browser das Sprach-Token durch.',
  ],
  [
    /first[_ ]message/i,
    'Im Agenten unter Sicherheit → Overrides „First message" freigeben und neu veröffentlichen. Damit begrüßt K4ia beim Wiederaufnehmen nicht ein zweites Mal.',
  ],
  [
    /signed url|expired/i,
    'Die signierte Adresse gilt 15 Minuten. Seite neu laden, um eine frische zu holen.',
  ],
]

/**
 * Übersetzt den Schließcode eines WebSocket in etwas, mit dem man arbeiten
 * kann. Gibt `null` zurück, wenn das Schließen normal war (Code 1000) — dann
 * ist es kein Fehler, sondern das Ende des Gesprächs.
 *
 * Die Codes 1002/1003/1008/1011 kommen von der Gegenstelle und tragen meist
 * einen Klartextgrund mit; der ist wertvoller als jede Vermutung von uns.
 */
export function describeCloseEvent(event: {
  code: number
  reason?: string
  wasClean?: boolean
}): string | null {
  // 1005 heißt „kein Status mitgeschickt" — so meldet der Browser ein
  // Schließen ohne Code, meist unser eigenes. Kein Fehler.
  if (event.code === 1000 || event.code === 1005) return null

  const grund = event.reason?.trim()

  // Nennt die Gegenstelle einen Grund, ist das die Auskunft — Punkt. Eine
  // Vermutung danebenzustellen macht sie schlechter, nicht besser: Beim ersten
  // echten Gespräch meldete ElevenLabs „Custom LLM extra body override is not
  // allowed", und unser Zusatz behauptete daneben, es liege wohl an einer
  // abgelaufenen Adresse. Geraten wird nur, wo nichts dasteht.
  if (grund) {
    const hilfe = HILFE_ZU_GRUND.find(([muster]) => muster.test(grund))?.[1]
    return `Sprachverbindung geschlossen (Code ${event.code}): ${grund}${hilfe ? ` ${hilfe}` : ''}`
  }

  const ohneGrund: Record<number, string> = {
    1006: ' Die Gegenstelle hat keinen Grund genannt. Meist: signierte Adresse abgelaufen (sie gilt 15 Minuten) oder der Agent ist nicht veröffentlicht.',
    1008: ' ElevenLabs hat die Verbindung abgelehnt, ohne einen Grund zu nennen.',
    1011: ' Fehler auf Seiten von ElevenLabs.',
  }
  return `Sprachverbindung geschlossen (Code ${event.code}).${ohneGrund[event.code] ?? ''}`
}

/**
 * Lautstärke eines Audio-Blocks als Wert zwischen 0 und 1, für die Anzeige.
 *
 * Effektivwert statt Spitzenwert, weil er ruhiger ist und nicht bei jedem
 * Knacken ausschlägt. Die Skalierung ist bewusst großzügig: Normale
 * Sprachlautstärke soll deutlich sichtbar sein, nicht erst Anschreien.
 */
export function micLevel(samples: Float32Array | number[]): number {
  let summe = 0
  for (let i = 0; i < samples.length; i++) summe += samples[i]! * samples[i]!
  const effektivwert = Math.sqrt(summe / (samples.length || 1))
  return Math.min(1, effektivwert * 8)
}

/**
 * Rechnet einen Audioblock von einer Abtastrate auf eine andere um.
 *
 * Lineare Interpolation, keine Filterung vorweg. Für Sprache, die anschließend
 * ohnehin durch eine Erkennung geht, ist das ausreichend und billig; wer
 * Musikqualität bräuchte, bräuchte einen Tiefpass davor.
 *
 * Gleiche Rate heißt: unverändert durchreichen, nicht vorsichtshalber rechnen.
 */
export function resample(input: Float32Array, von: number, nach: number): Float32Array {
  if (von === nach || input.length === 0) return input

  const verhaeltnis = von / nach
  const laenge = Math.floor(input.length / verhaeltnis)
  const out = new Float32Array(laenge)

  for (let i = 0; i < laenge; i++) {
    const pos = i * verhaeltnis
    const links = Math.floor(pos)
    const rechts = Math.min(links + 1, input.length - 1)
    const anteil = pos - links
    out[i] = input[links]! * (1 - anteil) + input[rechts]! * anteil
  }
  return out
}
